import { Input, message } from 'antd';
import dayjs from 'dayjs';
import { FC, ReactNode, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useClockContext } from '../app';
import { LayoutSite } from '../components/layout';
import { Loader } from '../components/loader';
import './selector.scss';
import { get_checkimport, get_file_races, get_fileinfos, get_sourcefiles, get_sources, post_doimport } from '../services/api.service';
import { ApiCentralizerRace, ApiFileInfos } from '../types';

/* -------------------------------------------------------------------------------- *\
|                               SelectorPage
\* -------------------------------------------------------------------------------- */
export const SelectorPage: FC<{ children?: ReactNode }> = ({ children }) =>
{
    const [loading, setLoading] = useState(true);
    const [sources, setSources] = useState<string[]>([]);
    const [files, setFiles] = useState<string[]>([]);
    const [selsource, setSelSource] = useState<string>('');
    const [selfile, setSelFile] = useState<string>('');
    const [importname, setImportName] = useState<string>('');
    const [importstatus, setImportStatus] = useState<string>('');
    const [fileinfos, setFileInfos] = useState<ApiFileInfos | null>(null);
    const [fileinfosloading, setFileInfosLoading] = useState<boolean>(false);
    const [races, setRaces] = useState<ApiCentralizerRace[]>([]);
    const [racesloading, setRacesLoading] = useState<boolean>(false);
    const [selrace, setSelRace] = useState<ApiCentralizerRace | null>(null);
    const selfileref = useRef<string>('');
    const racesref = useRef<HTMLDivElement>(null);
    const clock = useClockContext();

    const navigate = useNavigate();

    /* -------------------------------------------------------------------------------- *\
    |                               refreshSources
    \* -------------------------------------------------------------------------------- */
    const refreshSources = async () =>
    {
        setSelSource('');
        setSelFile('');
        setFiles([]);
        setImportStatus('');
        get_sources().then((sources: string[]) =>
        {
            setSources(sources);
        })
        .catch(error =>
        {
            message.error(String(error));
            if (error.status === 403) navigate('/login');
        });
    }
    /* -------------------------------------------------------------------------------- *\
    |                               selectFile
    \* -------------------------------------------------------------------------------- */
    const selectFile = (file: string) =>
    {
        selfileref.current = file;
        setSelFile(file);
        setFileInfos(null);
        setFileInfosLoading(true);
        setRaces([]);
        setSelRace(null);
        get_fileinfos(selsource, file)
            .then(infos =>
            {
                if (selfileref.current !== file) return;
                setFileInfos(infos);
                if (infos && infos.t_min && infos.t_max) findRaces(selsource, file, infos);
            })
            .catch(error => message.error(String(error)))
            .finally(() => setFileInfosLoading(false));
    }
    /* -------------------------------------------------------------------------------- *\
    |                               findRaces
    |   Centralizer races that may match the file, the first one covered by the
    |   file time range is preselected.
    \* -------------------------------------------------------------------------------- */
    const findRaces = (source: string, file: string, infos: ApiFileInfos) =>
    {
        setRacesLoading(true);
        get_file_races(source, infos.t_min, infos.t_max)
            .then(races =>
            {
                if (selfileref.current !== file) return;
                setRaces(races);
                const best = races.find(r => r.contains);
                if (best) chooseRace(best);
            })
            .catch(error => message.error(String(error)))
            .finally(() => setRacesLoading(false));
    }
    /* -------------------------------------------------------------------------------- *\
    |                               chooseRace
    \* -------------------------------------------------------------------------------- */
    const chooseRace = (race: ApiCentralizerRace) =>
    {
        setSelRace(race);
        setImportName(`${dayjs(race.start).format('YYYYMMDD')}${race.sic}${race.number}`);
    }
    /* -------------------------------------------------------------------------------- *\
    |                               expand
    \* -------------------------------------------------------------------------------- */
    const expand = (source: string) =>
    {
        setLoading(true);
        setSelSource('');
        setSelFile('');
        setFiles([]);
        setImportStatus('');
        setFileInfos(null);
        setRaces([]);
        setSelRace(null);
        selfileref.current = '';
        get_sourcefiles(source).then((files: string[]) =>
        {
            files = files.sort((a, b) => -a.localeCompare(b));
            setSelSource(source);
            setFiles(files);
        })
        .catch(error =>
        {
            message.error(String(error));
            if (error.status === 403) navigate('/login');
        })
        .finally(() => setLoading(false));
    }
    /* -------------------------------------------------------------------------------- *\
    |                               doImport
    \* -------------------------------------------------------------------------------- */
    const doImport = () =>
    {
        setLoading(true);
        post_doimport(selsource, selfile, importname).then(() =>
        {
            message.info('Import started');
            setImportStatus('Importation started');

            const interval = setInterval(() =>
            {
                get_checkimport().then((state: string) =>
                {
                    if (state === "done")
                    {
                        clearInterval(interval);
                        message.info('Imported');
                        setImportStatus('Importation succeeded');
                        setLoading(false);
                    }
                    else if (state === "error")
                    {
                        clearInterval(interval);
                        setLoading(false);
                        setImportStatus('Importation failed');
                        message.info('Importation failed');
                        setLoading(false);
                    }
                    else
                    {
                        setImportStatus(state);
                    }
                })
                .catch(error =>
                {
                    message.error(String(error));
                    if (error.status === 403) navigate('/login');
                });
            }, 500);
        })
        .catch(error =>
        {
            setImportStatus(String(error));
            message.error(String(error));
            if (error.status === 403) navigate('/login');
        });
    }

    /* -------------------------------------------------------------------------------- *\
    |                               effects
    \* -------------------------------------------------------------------------------- */
    useEffect(() => {
        setLoading(true);
        refreshSources().then(() => setLoading(false));
        // eslint-disable-next-line
    }, [])

    // center the selected race in the races list
    useEffect(() => {
        const container = racesref.current;
        const item = container?.querySelector<HTMLElement>('.selector__body__import__races__item--selected');
        if (!container || !item) return;
        const c = container.getBoundingClientRect();
        const i = item.getBoundingClientRect();
        container.scrollTop += (i.top - c.top) - (c.height - i.height) / 2;
    }, [selrace, races])

    return (
        <LayoutSite>
            <div className="selector">
                {loading ? <Loader /> : <></>}
                <div className="selector__header">
                    <div>Datas import</div>
                    <div className='calendar__header__clock'>{clock}</div>
                </div>
                <div className="selector__body">
                    <div>
                        <div className="selector__body__sources">
                            <div className="selector__body__sources__header">Servers</div>
                            <div className="selector__body__sources__table">
                            {
                                sources.map((source, is) => (
                                    <div className="selector__body__sources__table__item" key={'source' + is} onClick={() => expand(source)}>{source}</div>
                                ))
                            }
                            </div>
                        </div>
                    </div>

                {
                    selsource &&
                    (
                        <div className="selector__body__files">
                            <div className="selector__body__files__header">Files of {selsource}</div>
                            <div className="selector__body__files__table">
                            {
                                files.map((file, is) => (
                                    <div className={`selector__body__files__table__item${file === selfile ? '--selected' : ''}`} key={'file' + is} onClick={() => selectFile(file)}>{file}</div>
                                ))
                            }
                            </div>
                        </div>
                    )
                }

                {
                    selfile &&
                    (
                        <div className="selector__body__import">
                            <div className="selector__body__import__header">Import</div>
                            <div className="selector__body__import__row">
                                <div className="selector__body__import__row__label">source</div>
                                <div className="selector__body__import__row__value">{selsource}</div>
                            </div>
                            <div className="selector__body__import__row">
                                <div className="selector__body__import__row__label">file</div>
                                <div className="selector__body__import__row__value">{selfile}</div>
                            </div>
                            <div className="selector__body__import__row">
                                <div className="selector__body__import__row__label">name</div>
                                <div className="selector__body__import__row__value"><Input type="text" value={importname} onChange={(e) => setImportName(e.target.value)}/></div>
                            </div>
                            <div className="selector__body__import__row">
                                <div className="selector__body__import__row__button" onClick={() => doImport()}>Import</div>
                            </div>
                            <div className="selector__body__import__row" style={{marginBottom: '10px'}}>
                                <div className="selector__body__import__row__message">{importstatus}</div>
                            </div>
                            {fileinfosloading && (
                                <div className="selector__body__import__row">
                                    <div className="selector__body__import__row__label">infos</div>
                                    <div className="selector__body__import__row__value">loading…</div>
                                </div>
                            )}
                            {fileinfos && (
                                <>
                                    <div className="selector__body__import__row">
                                        <div className="selector__body__import__row__label">début</div>
                                        <div className="selector__body__import__row__value">{new Date(fileinfos.t_min).toLocaleString()}</div>
                                    </div>
                                    <div className="selector__body__import__row">
                                        <div className="selector__body__import__row__label">fin</div>
                                        <div className="selector__body__import__row__value">{new Date(fileinfos.t_max).toLocaleString()}</div>
                                    </div>
                                    <div className="selector__body__import__races" ref={racesref}>
                                        <div className="selector__body__import__races__header">courses</div>
                                        {racesloading && <div className="selector__body__import__races__empty">loading…</div>}
                                        {!racesloading && races.length === 0 && <div className="selector__body__import__races__empty">no race found</div>}
                                        {races.map(race => (
                                            <div key={`${race.sic}-${race.meeting}-${race.number}-${race.start}`}
                                                className={`selector__body__import__races__item${race === selrace ? '--selected' : ''}${race.contains ? '' : ' selector__body__import__races__item--outside'}`}
                                                title={`${race.name} (${race.source})`}
                                                onClick={() => chooseRace(race)}>
                                                {dayjs(race.start).format('HH:mm')} {race.sic} R{race.meeting}C{race.number} {race.trackname}
                                            </div>
                                        ))}
                                    </div>
                                </>
                            )}
                            {fileinfos && fileinfos.liste_fusion.length > 0 && (
                                <table className="selector__body__import__fusions">
                                    <thead>
                                        <tr>
                                            <th>Fusion</th>
                                            <th>Capteurs</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {fileinfos.liste_fusion
                                            .map((fusionId, i) => ({ fusionId, sensors: (fileinfos.id_fusion[i] ?? []).filter(s => s !== -1) }))
                                            .sort((a, b) => a.fusionId - b.fusionId)
                                            .map(({ fusionId, sensors }) => (
                                                <tr key={fusionId}>
                                                    <td>{fusionId}</td>
                                                    <td>{sensors.join(', ')}</td>
                                                </tr>
                                            ))
                                        }
                                    </tbody>
                                </table>
                            )}
                        </div>
                    )
                }
                </div>
            </div>
        </LayoutSite>
    );
}
